import React, { useState } from "react";
import { motion } from "framer-motion";
import { AudioWaveform, Play, X } from "lucide-react";
import { IDEVRA } from "@/data/idevra";
import { sfx } from "@/lib/sound";

const SYSTEM = [
  { id: "boot", name: "BOOT CHIME", desc: "Console power-on", fn: () => sfx.boot() },
  { id: "launch", name: "LAUNCH CHIME", desc: "Logo light-burst", fn: () => sfx.psLaunch() },
  { id: "tick", name: "TICK", desc: "Focus move", fn: () => sfx.tick() },
  { id: "select", name: "SELECT", desc: "Confirm press", fn: () => sfx.clank() },
  { id: "back", name: "BACK", desc: "Back out", fn: () => sfx.back() },
  { id: "whoosh", name: "WHOOSH", desc: "Panel transition", fn: () => sfx.whoosh() },
  { id: "trophy", name: "TROPHY", desc: "Unlock ding", fn: () => sfx.trophy() },
  { id: "konami", name: "KONAMI", desc: "Secret code", fn: () => sfx.konami() },
];

const THEMES = ["classic", "modern", "melody"];
const THEME_LABELS = { classic: "CLASSIC", modern: "MODERN", melody: "MUSIC BOX" };

const PRESETS = [
  { id: "rainy-morning", name: "RAINY MORNING", mix: { pour: 35, trickle: 50, plonk: 25, birds: 80, rain: 75, crickets: 0 } },
  { id: "zen-noon", name: "ZEN NOON", mix: { pour: 100, trickle: 100, plonk: 100, birds: 100, rain: 0, crickets: 0 } },
  { id: "evening-crickets", name: "EVENING CRICKETS", mix: { pour: 45, trickle: 60, plonk: 55, birds: 15, rain: 0, crickets: 85 } },
];

const Row = ({ idx, name, desc, testid, active, onPlay }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onPlay}
    className={`grid w-full grid-cols-[2rem_1fr_auto] items-center gap-3 border-b border-white/60 px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-white/70 sm:px-6 ${
      active ? "bg-white/80" : ""
    }`}
  >
    <span className="f-orbitron text-[10px] text-[#94A3B8]">{String(idx).padStart(2, "0")}</span>
    <span className="min-w-0">
      <span className={`block f-mono text-[10px] tracking-[0.25em] ${active ? "text-[#0284C7]" : "text-[#0F172A]"}`}>{name}</span>
      <span className="text-[10px] text-[#94A3B8]">{desc}</span>
    </span>
    <Play size={13} className={active ? "text-[#0284C7]" : "text-[#94A3B8]"} />
  </button>
);

export default function SoundTestRoom({ onClose }) {
  const [theme, setTheme] = useState(sfx.theme);
  const [playing, setPlaying] = useState(null);
  const [mix, setMix] = useState(() => ({
    pour: Math.round(sfx.waterMix.pour * 100),
    trickle: Math.round(sfx.waterMix.trickle * 100),
    plonk: Math.round(sfx.waterMix.plonk * 100),
    birds: Math.round(sfx.waterMix.birds * 100),
    rain: Math.round(sfx.waterMix.rain * 100),
    crickets: Math.round(sfx.waterMix.crickets * 100),
  }));
  const [preset, setPreset] = useState(null);
  const [auto, setAuto] = useState(sfx.gardenAuto);

  const readMix = () => ({
    pour: Math.round(sfx.waterMix.pour * 100),
    trickle: Math.round(sfx.waterMix.trickle * 100),
    plonk: Math.round(sfx.waterMix.plonk * 100),
    birds: Math.round(sfx.waterMix.birds * 100),
    rain: Math.round(sfx.waterMix.rain * 100),
    crickets: Math.round(sfx.waterMix.crickets * 100),
  });

  const setMixPart = (part, v) => {
    setPreset(null);
    if (auto) {
      setAuto(false);
      sfx.setGardenAuto(false);
    }
    setMix((m) => ({ ...m, [part]: v }));
    sfx.setWaterMix(part, v / 100);
  };

  const applyPreset = (p) => {
    setPreset(p.id);
    if (auto) {
      setAuto(false);
      sfx.setGardenAuto(false);
    }
    setMix(p.mix);
    Object.entries(p.mix).forEach(([part, v]) => sfx.setWaterMix(part, v / 100));
    sfx.clank();
    if (p.mix.crickets > 0) sfx.crickets();
    else if (p.mix.birds >= 80) sfx.birds();
  };

  const enableAuto = () => {
    setPreset(null);
    setAuto(true);
    sfx.setGardenAuto(true);
    setMix(readMix());
    sfx.clank();
    if (sfx.waterMix.crickets > 0) sfx.crickets();
    else sfx.birds();
  };

  const pickTheme = (id) => {
    const wasOn = !!sfx.ambient;
    sfx.setTheme(id);
    setTheme(id);
    if (wasOn) {
      sfx.stopAmbient();
      sfx.startAmbient();
    }
    sfx.clank();
  };

  const play = (id, fn) => {
    setPlaying(id);
    fn();
  };

  return (
    <motion.div
      className="absolute inset-0 z-40 flex items-center justify-center p-4 sm:p-8"
      data-testid="sound-test-room"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#F1F5F9]/70 backdrop-blur-md"
        data-testid="sound-test-backdrop"
      />
      <motion.div
        className="iv-glass iv-overlay-card relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl"
        initial={{ scale: 0.93, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 16, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
      >
        <div className="flex items-center justify-between border-b border-white/70 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0284C7]/10">
              <AudioWaveform size={16} color="#0284C7" />
            </span>
            <div>
              <div className="f-syncopate text-[11px] tracking-[0.3em] text-[#0F172A]">SOUND TEST ROOM</div>
              <div className="f-mono text-[8px] tracking-[0.25em] text-[#94A3B8]">AUDIO SUBSYSTEM · DIAGNOSTIC MODE</div>
            </div>
          </div>
          <button
            type="button"
            data-testid="sound-test-close"
            onClick={onClose}
            aria-label="Close sound test"
            className="iv-glass flex h-9 w-9 items-center justify-center rounded-full text-[#475569] transition-colors hover:text-[#0284C7]"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-white/60 px-6 py-3">
          <span className="f-mono text-[8px] tracking-[0.3em] text-[#94A3B8]">THEME:</span>
          {THEMES.map((id) => (
            <button
              key={id}
              type="button"
              data-testid={`sound-test-theme-${id}`}
              onClick={() => pickTheme(id)}
              className={`iv-chip !py-1 !text-[9px] transition-colors ${
                theme === id ? "!text-[#0284C7] shadow-[inset_0_0_0_1.5px_rgba(2,132,199,0.5)]" : ""
              }`}
            >
              {THEME_LABELS[id]}
            </button>
          ))}
        </div>

        {theme === "classic" && (
          <div className="border-b border-white/60 px-6 py-3" data-testid="water-mix-section">
            <div className="f-mono pb-2.5 text-[8px] tracking-[0.35em] text-[#94A3B8]">
              WATER GARDEN MIX
            </div>
            <div className="flex flex-wrap gap-2 pb-3">
              <button
                type="button"
                data-testid="preset-auto-btn"
                onClick={enableAuto}
                className={`iv-chip !py-1.5 !text-[9px] transition-colors hover:text-[#0284C7] ${
                  auto ? "!text-[#0284C7] shadow-[inset_0_0_0_1.5px_rgba(2,132,199,0.5)]" : ""
                }`}
              >
                AUTO · TIME OF DAY
              </button>
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`preset-${p.id}-btn`}
                  onClick={() => applyPreset(p)}
                  className={`iv-chip !py-1.5 !text-[9px] transition-colors hover:text-[#0284C7] ${
                    preset === p.id ? "!text-[#0284C7] shadow-[inset_0_0_0_1.5px_rgba(2,132,199,0.5)]" : ""
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3">
              {[
                ["pour", "POUR"],
                ["trickle", "TRICKLE"],
                ["plonk", "PLONK"],
                ["birds", "BIRDS"],
                ["rain", "RAIN"],
                ["crickets", "CRICKETS"],
              ].map(([id, label]) => (
                <div key={id}>
                  <div className="f-mono pb-1.5 text-[8px] tracking-[0.25em] text-[#475569]">
                    {label} · {mix[id]}%
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={mix[id]}
                    data-testid={`watermix-${id}-slider`}
                    aria-label={`${label} level`}
                    onChange={(e) => setMixPart(id, Number(e.target.value))}
                    onPointerUp={() => {
                      if (id === "plonk") sfx.plonk();
                      if (id === "birds") sfx.birds();
                      if (id === "crickets") sfx.crickets();
                    }}
                    className="iv-volume-slider w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="iv-panel-scroll flex-1 overflow-y-auto">
          <div className="f-mono px-6 pt-4 pb-1.5 text-[8px] tracking-[0.35em] text-[#0284C7]">SYSTEM SOUNDS</div>
          <div>
            {SYSTEM.map((s, i) => (
              <Row
                key={s.id}
                idx={i + 1}
                name={s.name}
                desc={s.desc}
                testid={`play-${s.id}-btn`}
                active={playing === s.id}
                onPlay={() => play(s.id, s.fn)}
              />
            ))}
          </div>
          <div className="f-mono px-6 pt-5 pb-1.5 text-[8px] tracking-[0.35em] text-[#0284C7]">VENTURE JINGLES</div>
          <div className="pb-4">
            {IDEVRA.ventures.map((v, i) => (
              <Row
                key={v.slug}
                idx={SYSTEM.length + i + 1}
                name={v.name.toUpperCase()}
                desc={v.sector}
                testid={`play-jingle-${v.slug}-btn`}
                active={playing === v.slug}
                onPlay={() => play(v.slug, () => sfx.jingle(v.slug))}
              />
            ))}
          </div>
        </div>

        <div className="border-t border-white/70 px-6 py-3 f-mono text-[8px] tracking-[0.25em] text-[#94A3B8]">
          ○ BACK / ESC TO EXIT · THEME CHOICE IS SAVED
        </div>
      </motion.div>
    </motion.div>
  );
}
